import { EnrichedOp, FarolCor, SituacaoOp, UltimaImportacao } from '@/domain/ops/interfaces/ops';

export const FAROL_CORES: Array<FarolCor> = ['VERMELHO', 'AMBAR', 'VERDE', 'CINZA'];

export function isFarolCor(value: unknown): value is FarolCor {
  return typeof value === 'string' && (FAROL_CORES as Array<string>).includes(value);
}

/**
 * The portfolio first: the exits are the exception somebody goes looking for, and the
 * deleted ones are rarer still (today only undoing an import puts an OP there).
 */
export const SITUACOES: Array<SituacaoOp> = ['carteira', 'saidas', 'excluidas'];

export function isSituacaoOp(value: unknown): value is SituacaoOp {
  return typeof value === 'string' && (SITUACOES as Array<string>).includes(value);
}

/**
 * A recorded day, and not just "the field came back". An OP saved before these
 * fields existed arrives with them absent, and `!== null` reads absent as a real
 * exit: that is what showed the exit block, with an empty date and a raw reason,
 * on every active OP. Only a filled day means the OP left (or came back).
 */
export function temDia(dia: string | null | undefined): boolean {
  return typeof dia === 'string' && dia.trim() !== '';
}

export function saiuDaCarteira(op: Pick<EnrichedOp, 'saidaEm'>): boolean {
  return temDia(op.saidaEm);
}

export function voltouParaCarteira(op: Pick<EnrichedOp, 'retornoEm'>): boolean {
  return temDia(op.retornoEm);
}

/**
 * An OP REALLY in the trash. Same reason as `saiuDaCarteira`: an OP stored before the
 * field existed arrives without `arquivadoEm`, and `!== null` read absent as deleted,
 * which showed the "excluded" block (and hid "take out of the portfolio") on every
 * active OP of the portfolio.
 */
export function estaExcluida(op: Pick<EnrichedOp, 'arquivadoEm'>): boolean {
  return temDia(op.arquivadoEm);
}

/**
 * An import that was REALLY undone. Same reason as `saiuDaCarteira`: a receipt stored
 * before the undo existed comes back without the field, and `!== null` read absent as
 * undone, which printed "Desfeita em - por ." on every import older than the feature.
 */
export function foiDesfeita(importacao: Pick<UltimaImportacao, 'desfeitaEm'>): boolean {
  return temDia(importacao.desfeitaEm);
}

/**
 * API dates are `YYYY-MM-DD`; parsed as a LOCAL date so the day never shifts by
 * timezone. Used by the Excel export, where the cell has to carry a real date.
 */
export function parseIsoDate(date: string | null | undefined): Date | null {
  if (date === null || date === undefined || date === '') {
    return null;
  }
  const [year, month, day] = date.split('-').map(Number);
  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) {
    return null;
  }
  return new Date(year, month - 1, day);
}

/**
 * What every reading with nothing to show prints. A missing value is a dash on the
 * screen, never an exception: the ficha has to open even on the OP that predates the
 * field.
 */
export const SEM_VALOR = '-';

export function formatDate(date: string | null | undefined): string {
  if (date === null || date === undefined || date === '') {
    return SEM_VALOR;
  }
  // Only the day part. Most dates here are already `YYYY-MM-DD`, but the ones that come
  // from a timestamp (the import receipt, the deletion of an OP) arrive as a full ISO
  // string, and splitting that one on `-` printed `15T01:16:05.766Z/10/2026`.
  const [year, month, day] = date.slice(0, 10).split('-');
  return `${day}/${month}/${year}`;
}

/**
 * `dd/mm/aa` for table columns: the century is noise in a cell that has to stay
 * narrow enough for the whole table to fit on the screen. Returns `null` when there
 * is no date, so the cell renders the table's own dash.
 */
export function formatShortDate(date: string | null | undefined): string | null {
  if (date === null || date === undefined || date === '') {
    return null;
  }
  const [year, month, day] = date.split('-');
  if (year === undefined || month === undefined || day === undefined) {
    return null;
  }
  return `${day}/${month}/${year.slice(-2)}`;
}

/**
 * A number that is REALLY a number, the numeric twin of `temDia`. An OP saved before a
 * field existed comes back without it, and `=== null` reads absent as "filled": that is
 * what sent the ficha into the "corrected quantity" branch of an OP that has no
 * correction, where formatting `undefined` broke the whole panel.
 */
export function temNumero(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

export function formatCurrency(value: number | null | undefined): string {
  if (!temNumero(value)) {
    return SEM_VALOR;
  }
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function formatNumber(value: number | null | undefined): string {
  if (!temNumero(value)) {
    return SEM_VALOR;
  }
  return value.toLocaleString('pt-BR');
}

/** Casas decimais dos dias de producao: o suficiente para "meio dia" ler como 0,5. */
const DIAS_PRODUCAO_DECIMAIS = 1;

/**
 * Dias de producao, que desde a fila em minutos corridos sao FRACIONARIOS: uma OP que
 * ocupa meia celula por um dia vale 0,5, e nao 1. Arredondar para cima aqui esconderia
 * de novo a sobra que o motor passou a aproveitar, entao a tela mostra a fracao.
 */
export function formatDiasProducao(dias: number | null): string | null {
  if (dias === null || !Number.isFinite(dias)) {
    return null;
  }
  return dias.toLocaleString('pt-BR', { maximumFractionDigits: DIAS_PRODUCAO_DECIMAIS });
}

/** Casas de uma carga em por cento: "112,4%" ja diz tudo, "112,43999%" nao diz mais. */
const PERCENTUAL_DECIMAIS = 1;

/**
 * Um por cento como a pessoa o le: virgula e no maximo uma casa, entao o limite
 * redondo sai "100%" e a carga quebrada sai "112,4%". O numero ja vem pronto da API
 * (quem calcula e o motor), aqui so se escreve.
 */
export function formatPercent(value: number | null | undefined): string {
  if (!temNumero(value)) {
    return SEM_VALOR;
  }
  return value.toLocaleString('pt-BR', { maximumFractionDigits: PERCENTUAL_DECIMAIS });
}
